import { useEffect, useRef } from 'react'
import { useAuthContext } from 'shared/components/AuthContext'
import { useSessionExpiresAt } from 'shared/components/SessionExpiresAtContext'
import type { FirebaseAuth } from 'shared/services'
import {
  getNewestAuthTime,
  getSharedAuthTime,
  isSessionExpired,
  runWhenTimeReached,
  useViewerMode,
  ViewerMode,
} from 'shared/utils'

// The token is read again, as the session may have been extended, also in
// another tab, before its new expiry was loaded. If it can't be read, the auth
// time shared by the other tabs is checked, and without it the loaded expiry is
// trusted.
const readIsSessionExpired = async (
  auth: FirebaseAuth,
  uid: string,
  mode: ViewerMode
) => {
  const authTime = await auth.getAuthTime().catch(() => undefined)
  const newestAuthTime = authTime
    ? getNewestAuthTime(uid, authTime)
    : getSharedAuthTime(uid)

  return !newestAuthTime || isSessionExpired(newestAuthTime, mode)
}

// While paused, an expired session isn't ended. The expiry is checked again
// once the pause ends.
export const useSessionExpiry = (
  onExpire: (mode: ViewerMode) => Promise<void>,
  isPaused = false
) => {
  const { user, auth } = useAuthContext()
  const { mode } = useViewerMode()
  const sessionExpiresAt = useSessionExpiresAt()
  const onExpireRef = useRef(onExpire)

  onExpireRef.current = onExpire

  useEffect(() => {
    if (!user || !auth || isPaused || sessionExpiresAt === undefined) return

    let cancelled = false
    let isExpiring = false

    const expire = async () => {
      if (isExpiring) return
      isExpiring = true

      try {
        const isExpired = await readIsSessionExpired(auth, user.uid, mode)
        if (cancelled || !isExpired) {
          isExpiring = false
          return
        }
        await onExpireRef.current(mode)
      } catch {
        // Retried when the user comes back to the tab
        isExpiring = false
      }
    }

    const stopExpiry = runWhenTimeReached(sessionExpiresAt, expire)

    return () => {
      cancelled = true
      stopExpiry()
    }
  }, [auth, isPaused, mode, sessionExpiresAt, user])
}
